import React from "react";
import type { SectionComponentProps } from "../../registry.js";

interface ParallaxBreakProps {
  quote?: string;
  attribution?: string;
  imageUrl?: string;
  /** 0–1. How dark the overlay is. Default 0.45. */
  overlayOpacity?: number;
}

export function ParallaxBreak({ props }: SectionComponentProps) {
  const {
    quote = "Food is our common ground, a universal experience.",
    attribution = "James Beard",
    imageUrl,
    overlayOpacity = 0.45,
  } = props as ParallaxBreakProps;

  return (
    <section className="relative h-[60vh] overflow-hidden" id="parallax-break">
      {/*
        data-parallax — this element is 140% tall so it can travel ±60px
        without exposing empty space. GSAP moves it from y:-60 to y:+60
        as the section scrolls through the viewport (scrub).
      */}
      <div
        data-parallax
        className="absolute left-0 right-0"
        style={{ top: "-10%", height: "120%", willChange: "transform" }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          /*
            Two-layer background: a deep gradient base + a lighter angled
            overlay so the vertical drift is clearly visible.
          */
          <div
            className="w-full h-full"
            style={{
              background: `
                linear-gradient(160deg, oklch(0.3 0.15 var(--color-primary, 260)) 0%, oklch(0.12 0.08 var(--color-primary, 260)) 100%)
              `,
            }}
          >
            {/* Decorative angled stripe — moves with the background, making drift obvious */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                background:
                  "repeating-linear-gradient(135deg, transparent 0px, transparent 60px, rgba(255,255,255,0.15) 60px, rgba(255,255,255,0.15) 62px)",
              }}
            />
          </div>
        )}
      </div>

      {/* Dark overlay — stays fixed in the section */}
      <div
        className="absolute inset-0"
        style={{ background: `rgba(0,0,0,${overlayOpacity})` }}
      />

      {/* Content — scrolls with the page, creating the parallax contrast */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center">
        <blockquote className="text-2xl md:text-4xl font-serif font-light leading-snug max-w-2xl text-white">
          &ldquo;{quote}&rdquo;
        </blockquote>
        {attribution && (
          <cite className="mt-5 text-sm text-white/55 not-italic tracking-widest uppercase">
            — {attribution}
          </cite>
        )}
      </div>
    </section>
  );
}
